import {isLoggedIn} from '@github-ui/client-env'
import {BlockUserFromOrgDialog} from '@github-ui/commenting/BlockUserFromOrgDialog'
import {unblockUserPersonally} from '@github-ui/commenting/mutations/unblock-user-personally-mutation'
import {PersonalBlockUserDialog} from '@github-ui/commenting/PersonalBlockUserDialog'
import {type SelectionContext, selectQuoteFromComment} from '@github-ui/commenting/quotes'
import {ReportContentDialog} from '@github-ui/commenting/ReportContentDialog'
import {UnblockUserFromOrgDialog} from '@github-ui/commenting/UnblockUserFromOrgDialog'
import {copyText} from '@github-ui/copy-to-clipboard'
import type {IssueViewerViewQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerViewQuery'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {
  BlockedIcon,
  KebabHorizontalIcon,
  LinkIcon,
  MarkdownIcon,
  PencilIcon,
  QuoteIcon,
  ReportIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, ConfirmationDialog, IconButton} from '@primer/react'
import {useMemo, useState} from 'react'

import {LABELS} from '../constants/labels'
import type {IssueBodyHeaderActionsNode} from './issue-body-header-actions-query-types'

export type IssueBodyHeaderActionsCommentFragment = Pick<IssueBodyHeaderActionsNode, 'body'>

type IssueViewerIssue = NonNullable<NonNullable<IssueViewerViewQueryQuery['repository']>['issue']>
type IssueViewerAuthor = NonNullable<IssueViewerIssue['author']>

export type IssueBodyHeaderActionsFragment = Pick<IssueViewerAuthor, 'id' | 'login'>

export type IssueBodyHeaderActionsProps = {
  url?: string
  issueBodyRef?: React.RefObject<HTMLDivElement | null>
  onReplySelect?: (quotedText?: string) => void
  startIssueBodyEdit: () => void
  onEditHover?: () => void
  viewerCanUpdate: boolean
  viewerCanReport?: boolean
  viewerCanReportToMaintainer?: boolean
  viewerCanBlockFromOrg?: boolean
  viewerCanUnblockFromOrg?: boolean
  viewerCanBlockPersonally?: boolean
  viewerCanUnblockPersonally?: boolean
  issueId?: string
  owner?: string
  repository?: string
  ownerId?: string
  ownerUrl?: string
  isDisabled?: boolean
  // Optional name of the portal container to render the action menu overlay into.
  actionMenuPortalContainerName?: string
}

type IssueBodyHeaderActionsInternalProps = IssueBodyHeaderActionsProps & {
  author: IssueBodyHeaderActionsFragment
  comment: IssueBodyHeaderActionsCommentFragment
}

export function IssueBodyHeaderActions({
  url,
  issueBodyRef,
  onReplySelect,
  startIssueBodyEdit,
  onEditHover,
  viewerCanUpdate,
  viewerCanReport,
  viewerCanReportToMaintainer,
  viewerCanBlockFromOrg,
  viewerCanUnblockFromOrg,
  viewerCanBlockPersonally,
  viewerCanUnblockPersonally,
  issueId,
  owner,
  repository,
  ownerId,
  ownerUrl,
  author: actorData,
  comment: commentData,
  isDisabled = false,
  actionMenuPortalContainerName,
}: IssueBodyHeaderActionsInternalProps) {
  const [menuOpened, setMenuOpened] = useState(false)
  const [selection, setSelection] = useState<SelectionContext | null>(null)
  const [isBlockUserFromOrgOpen, setIsBlockUserFromOrgOpen] = useState(false)
  const [isUnblockUserFromOrgOpen, setIsUnblockUserFromOrgOpen] = useState(false)
  const [isPersonalBlockOpen, setIsPersonalBlockOpen] = useState(false)
  const [isPersonalUnblockOpen, setIsPersonalUnblockOpen] = useState(false)
  const {addToast} = useToastContext()

  const reportUrl = useMemo(() => {
    if (!actorData || !url) return undefined
    const host = ssrSafeWindow?.location.origin
    if (!host) return undefined

    const query = `content_url=${encodeURIComponent(url)}&report=${actorData.login}+(user)`
    const reportTargetUrl = `${host}/contact/report-content?${query}`

    return reportTargetUrl
  }, [actorData, url])

  const copyToClipboard = (textToCopy: string) => {
    copyText(textToCopy)
  }

  const [isReportContentDialogOpen, setIsReportContentDialogOpen] = useState(false)

  const showReportSection = viewerCanReport || viewerCanReportToMaintainer

  const userCanBlockPersonally = canBlockPersonally(!!viewerCanBlockPersonally)
  const userCanUnblockPersonally = canUnblockPersonally(!!viewerCanUnblockPersonally)
  const userCanBlock = !userCanBlockPersonally && !userCanUnblockPersonally && canBlock(!!viewerCanBlockFromOrg)
  const userCanUnblock = !userCanBlockPersonally && !userCanUnblockPersonally && canUnblock(!!viewerCanUnblockFromOrg)

  return (
    <>
      {isReportContentDialogOpen && owner && repository && ownerUrl && issueId && (
        <ReportContentDialog
          owner={owner}
          repository={repository}
          ownerUrl={ownerUrl}
          reportUrl={reportUrl}
          contentId={issueId}
          onClose={() => setIsReportContentDialogOpen(false)}
          contentType={'issue'}
        />
      )}
      {isPersonalBlockOpen && actorData && url && issueId && (
        <PersonalBlockUserDialog
          contentId={issueId}
          contentAuthor={actorData}
          contentUrl={url}
          onClose={() => setIsPersonalBlockOpen(false)}
        />
      )}
      {isPersonalUnblockOpen && actorData && (
        <ConfirmationDialog
          title={`Unblock @${actorData.login}`}
          confirmButtonContent="Unblock user"
          confirmButtonType="danger"
          onClose={gesture => {
            if (gesture === 'confirm') {
              void (async () => {
                try {
                  await unblockUserPersonally(actorData.login)
                  window.location.reload()
                } catch {
                  // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
                  addToast({type: 'error', message: 'Failed to unblock user'})
                }
              })()
            }
            setIsPersonalUnblockOpen(false)
          }}
        >
          Are you sure you want to unblock @{actorData.login}?
        </ConfirmationDialog>
      )}
      {isBlockUserFromOrgOpen && owner && ownerId && url && issueId && (
        <BlockUserFromOrgDialog
          onClose={() => setIsBlockUserFromOrgOpen(false)}
          organization={{login: owner, id: ownerId}}
          contentId={issueId}
          contentAuthor={{login: actorData.login, id: actorData.id}}
          contentUrl={url}
        />
      )}
      {isUnblockUserFromOrgOpen && owner && ownerId && url && issueId && (
        <UnblockUserFromOrgDialog
          onClose={() => setIsUnblockUserFromOrgOpen(false)}
          organization={{login: owner, id: ownerId}}
          contentAuthor={{login: actorData.login, id: actorData.id}}
        />
      )}
      <ActionMenu
        open={menuOpened}
        onOpenChange={(value: boolean) => {
          if (value) {
            const current = window.getSelection()
            if (current && current.anchorNode) {
              setSelection({
                anchorNode: current.anchorNode,
                range: current.getRangeAt(0),
              })
            } else {
              setSelection(null)
            }
          }
          setMenuOpened(value)
        }}
      >
        <ActionMenu.Anchor>
          <IconButton
            disabled={isDisabled}
            size="small"
            icon={KebabHorizontalIcon}
            variant="invisible"
            aria-label={LABELS.issueBodyHeaderActions}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay portalContainerName={actionMenuPortalContainerName}>
          <ActionList>
            <ActionList.Item
              inactiveText={!url || url === '' ? 'URL must be available to be copied' : undefined}
              onSelect={() => copyToClipboard(url || '')}
            >
              <ActionList.LeadingVisual>
                <LinkIcon />
              </ActionList.LeadingVisual>
              Copy link
            </ActionList.Item>
            <ActionList.Item onSelect={() => copyToClipboard(commentData.body)}>
              <ActionList.LeadingVisual>
                <MarkdownIcon />
                {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */}
              </ActionList.LeadingVisual>
              Copy Markdown
            </ActionList.Item>
            {isLoggedIn() && (
              <ActionList.Item
                onSelect={() => {
                  const newValue = selectQuoteFromComment(issueBodyRef?.current, selection, commentData.body)
                  onReplySelect?.(newValue)
                }}
              >
                <ActionList.LeadingVisual>
                  <QuoteIcon />
                </ActionList.LeadingVisual>
                Quote reply
              </ActionList.Item>
            )}
            {viewerCanUpdate && (
              <>
                <ActionList.Divider />
                <ActionList.Item
                  onSelect={() => {
                    startIssueBodyEdit()
                  }}
                  onMouseEnter={onEditHover}
                >
                  <ActionList.LeadingVisual>
                    <PencilIcon />
                  </ActionList.LeadingVisual>
                  Edit
                </ActionList.Item>
              </>
            )}
            {showReportSection && (
              <>
                <ActionList.Divider />
                {viewerCanReportToMaintainer ? (
                  <ActionList.Item
                    onSelect={() => {
                      setIsReportContentDialogOpen(true)
                    }}
                  >
                    <ActionList.LeadingVisual>
                      <ReportIcon />
                    </ActionList.LeadingVisual>
                    Report content
                  </ActionList.Item>
                ) : (
                  <ActionList.LinkItem href={reportUrl} target="_blank">
                    <ActionList.LeadingVisual>
                      <ReportIcon />
                    </ActionList.LeadingVisual>
                    Report content
                  </ActionList.LinkItem>
                )}
              </>
            )}
            {(userCanBlockPersonally || userCanUnblockPersonally || userCanBlock || userCanUnblock) &&
              !showReportSection && <ActionList.Divider />}
            {userCanBlockPersonally && (
              <ActionList.Item onSelect={() => setIsPersonalBlockOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Block user
              </ActionList.Item>
            )}
            {userCanUnblockPersonally && (
              <ActionList.Item onSelect={() => setIsPersonalUnblockOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Unblock user
              </ActionList.Item>
            )}
            {userCanBlock && (
              <ActionList.Item
                onSelect={() => {
                  setIsBlockUserFromOrgOpen(true)
                }}
              >
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Block user
              </ActionList.Item>
            )}
            {userCanUnblock && (
              <ActionList.Item
                onSelect={() => {
                  setIsUnblockUserFromOrgOpen(true)
                }}
              >
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Unblock user
              </ActionList.Item>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}

function canBlock(viewerCanBlockFromOrg: boolean) {
  return viewerCanBlockFromOrg
}

function canUnblock(viewerCanUnblockFromOrg: boolean) {
  return viewerCanUnblockFromOrg
}

function canBlockPersonally(viewerCanBlockPersonally: boolean) {
  return viewerCanBlockPersonally
}

function canUnblockPersonally(viewerCanUnblockPersonally: boolean) {
  return viewerCanUnblockPersonally
}
